import { useState } from 'react'

import { QuestionInput } from '../QuestionInput/QuestionInput'
import { SuggestionChip } from '../SuggestionChip/SuggestionChip'
import classes from './QuestionBox.module.scss'

export type QuestionBoxProps = Readonly<{
  /** Example prompts as `{ id, label }` — the host resolves any i18n. */
  prompts: ReadonlyArray<{ id: string; label: string }>
  /** Called with the typed question or a picked prompt's label. */
  onAsk: (question: string) => void
  placeholder: string
  submitLabel: string
  betaLabel?: string
  testId?: string
  examplesTestId?: string
  chipTestId?: (id: string) => string
  inputTestId?: string
  submitTestId?: string
  inputName?: string
}>

/**
 * The entry surface: a {@link QuestionInput} plus a row of example
 * {@link SuggestionChip}s. Purely generic — it holds the draft question locally and
 * calls `onAsk`; the host decides what happens next (e.g. navigate to a chat page).
 */
export function QuestionBox({
  prompts,
  onAsk,
  placeholder,
  submitLabel,
  betaLabel,
  testId,
  examplesTestId,
  chipTestId,
  inputTestId,
  submitTestId,
  inputName,
}: QuestionBoxProps) {
  const [question, setQuestion] = useState('')

  const ask = (value: string) => {
    const trimmed = value.trim()

    if (trimmed) onAsk(trimmed)
  }

  return (
    <div className={classes.container} data-testid={testId}>
      <QuestionInput
        value={question}
        onChange={setQuestion}
        onSubmit={() => ask(question)}
        isLoading={false}
        placeholder={placeholder}
        submitLabel={submitLabel}
        betaLabel={betaLabel}
        animationDelayMs={1000}
        name={inputName}
        inputTestId={inputTestId}
        submitTestId={submitTestId}
      />
      {prompts.length > 0 && (
        <div className={classes.examples} data-testid={examplesTestId}>
          {prompts.map((prompt) => (
            <SuggestionChip
              key={prompt.id}
              label={prompt.label}
              onClick={() => ask(prompt.label)}
              testId={chipTestId?.(prompt.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
